import { useEffect, useRef, useState } from "react";
import { useModal } from "../../hooks";
import { useRecoilValue } from "recoil";
import { useLogin } from "../authentication/useLogin";
import { authState } from "../recoil";
import "../../assets/styles/timeoutConfig.css";

export function TimeoutConfig() {
    const modal = useModal();
    const auth = useRecoilValue(authState);
    const { logout, extendSession } = useLogin();

    const eventListenersRef = useRef<{ event: string; handler: () => void }[]>([]);
    const isModalOpenRef = useRef(false);

    // ===== CONFIG =====

    const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    const WARNING_TIMEOUT = 5 * 60 * 1000; // 5 minutes


    const MODAL_COUNTDOWN = 30; // max seconds shown in modal

    // ===== HELPERS =====
    const updateExpiredTime = () => {
        localStorage.setItem(
            "expireTime",
            (Date.now() + SESSION_TIMEOUT).toString()
        );
    };

    const forceLogout = () => {
        localStorage.removeItem("expireTime");
        logout();
    };

    const removeEventListeners = () => {
        eventListenersRef.current.forEach(({ event, handler }) => {
            window.removeEventListener(event, handler);
        });
    };

    const restoreEventListeners = () => {
        eventListenersRef.current.forEach(({ event, handler }) => {
            window.addEventListener(event, handler);
        });
    };

    // ===== MODAL =====
    const showWarningModal = (timeLeft: number) => {
        if (isModalOpenRef.current) return;

        isModalOpenRef.current = true;
        removeEventListeners();

        const initialSeconds = Math.min(
            MODAL_COUNTDOWN,
            Math.max(1, Math.floor(timeLeft / 1000))
        );

        const ModalCountdown = () => {
            const [seconds, setSeconds] = useState(initialSeconds);

            useEffect(() => {
                const interval = setInterval(() => {
                    setSeconds((prev) => {
                        if (prev <= 1) {
                            clearInterval(interval);
                            forceLogout();
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);

                return () => clearInterval(interval);
            }, []);

            const percent = (seconds / initialSeconds) * 100;
            const color =
                percent > 66 ? "#4caf50" :
                    percent > 33 ? "#ff9800" :
                        "#f44336";

            return (
                <div className="session-card">
                    <p className="session-text">Your session will expire in:</p>

                    <h2 className="session-timer" style={{ color }}>
                        {formatTime(seconds)}
                    </h2>

                    <div className="session-progress-wrapper">
                        <div
                            className="session-progress-bar"
                            style={{
                                width: `${percent}%`,
                                background: color,
                            }}
                        />
                    </div>

                    <p className="session-text-small">
                        Please extend your session to continue working.
                    </p>
                </div>
            );
        };

        modal.openModal({
            size: "xs",
            title: "⚠️ Session About to Expire",
            content: <ModalCountdown />,
            confirmText: "Extend Session",
            cancelText: "Logout Now",
            onConfirm: async () => {
                try {
                    await extendSession();
                    updateExpiredTime();
                    isModalOpenRef.current = false;
                    restoreEventListeners();
                } catch {
                    forceLogout();
                }
            },
            onCancel: forceLogout,
            topClose: false,
        });
    };

    // ===== INACTIVITY CHECK =====
    const checkForInactivity = () => {
        if (isModalOpenRef.current) return;

        const expireTime = localStorage.getItem("expireTime");
        if (!expireTime) return;

        const timeLeft = Number(expireTime) - Date.now();

        if (timeLeft <= WARNING_TIMEOUT && timeLeft > 0) {
            showWarningModal(timeLeft);
        } else if (timeLeft <= 0) {
            forceLogout();
        }
    };

    // ===== INTERVAL CHECKER =====
    useEffect(() => {
        if (!auth.isSignedIn) return;

        const interval = setInterval(checkForInactivity, 1000);
        return () => clearInterval(interval);
    }, [auth.isSignedIn]);

    // ===== ACTIVITY TRACKING =====
    useEffect(() => {
        if (!auth.isSignedIn) return;

        if (!localStorage.getItem("expireTime")) {
            updateExpiredTime();
        }

        const events = ["click", "keypress", "scroll", "mousemove"];
        const handlers = events.map((event) => ({
            event,
            handler: () => {
                if (!isModalOpenRef.current) {
                    updateExpiredTime();
                }
            },
        }));

        eventListenersRef.current = handlers;
        restoreEventListeners();

        return removeEventListeners;
    }, [auth.isSignedIn]);

    return null;
}

// ===== UTIL =====
function formatTime(seconds: number) {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
}
